import { useState, type DragEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { can } from '@seller/shared';
import {
  api,
  ApiError,
  REPORT_KINDS,
  type AnalysisStatus,
  type CoverageEntry,
  type ImportKindInfo,
  type ImportOutcome,
  type ReportFileView,
  type ReportKind,
  type SpapiStatus,
} from '../api/client.js';
import { useSession } from '../auth/useSession.js';
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  TableScroll,
  Tabs,
  type TabItem,
} from '../components/ui.js';
import { day, dateTime, fileSize, integer } from '../format.js';
import { datePolicyFor, useUploadQueue } from '../imports/UploadQueue.js';
import {
  listingSweepText,
  totals,
  type DatePolicy,
  type QueueItem,
  type QueueStatus,
} from '../lib/upload-queue.js';
import { SpapiConnectionCard } from '../components/SpapiConnectionCard.js';
import { chooserGroups, showFetchedNote } from '../lib/import-kinds.js';

/**
 * Report uploads. The kind chooser and multi-file queue live in a card at the
 * top (its state is held above the router, so a running batch survives leaving
 * the page). Below, one tab per kind shows how far that kind's data reaches,
 * the next download worth fetching, and everything loaded so far.
 */

export const KIND_LABELS: Record<ReportKind, string> = {
  all_listings: 'All Listings Report',
  all_orders: 'All Orders (flat file)',
  ledger_detail: 'Inventory Ledger (detailed view)',
  business_by_child_asin: 'Business Report: Sales and Traffic by Child Item',
  sales_traffic_json: 'SP-API Sales and Traffic (JSON)',
  inventory_health: 'FBA Inventory Health / Planning data',
  restock_recommendations: 'Restock Inventory recommendations',
  fba_inventory: 'FBA Manage Inventory',
};

const KIND_HINTS: Record<ReportKind, string> = {
  all_listings:
    'Products > Product Reports > All Listings Report (Custom), tab-delimited .txt. Only seller-sku is required; leave the rest of the default columns ticked (see docs/imports.md for the full list). Do not re-save it in Excel first: Excel rewrites long numeric identifiers, dates, and leading zeros, and may re-encode the file, so the saved copy is no longer what Amazon produced.',
  all_orders:
    'Orders > Order Reports > All Orders tab, as a text file. Cover two years in several downloads; overlaps are safe.',
  ledger_detail:
    'Supply Chain > Supply Chain Reports > Inventory Ledger > Download tab > Detailed View, then Request .csv Download. Overlapping ranges are safe.',
  business_by_child_asin:
    'Detail Page Sales and Traffic by Child Item (the by-SKU variant is accepted too), downloaded as CSV. Location in the new layout not yet confirmed. Give each file the date range you selected, one calendar month per file; the file does not carry it.',
  sales_traffic_json: 'The JSON body of GET_SALES_AND_TRAFFIC_REPORT from the Selling Partner API.',
  inventory_health:
    'Location unconfirmed in the new Seller Central layout, likely under Supply Chain > Supply Chain Reports > FBA Inventory Reports. Tab-delimited .txt; has sell-through and days-of-supply columns, not afn-fulfillable-quantity.',
  restock_recommendations: 'Supply Chain > Restock Inventory > Download report, CSV.',
  fba_inventory:
    'Supply Chain > Supply Chain Reports > FBA Inventory Reports > Manage FBA Inventory, tab-delimited .txt. What is at Amazon right now; upload one whenever stock levels matter.',
};

const STATUS_TONE: Record<ReportFileView['status'], string> = {
  parsed: 'text-ok-text',
  failed: 'text-danger-text',
  stored: 'text-muted',
};

const QUEUE_STATUS: Record<QueueStatus, { label: string; tone: string }> = {
  queued: { label: 'Queued', tone: 'text-muted' },
  uploading: { label: 'Uploading', tone: 'text-body' },
  imported: { label: 'Imported', tone: 'text-ok-text' },
  duplicate: { label: 'Duplicate', tone: 'text-muted' },
  failed: { label: 'Failed', tone: 'text-danger-text' },
  too_large: { label: 'Too large', tone: 'text-danger-text' },
};

const ACCEPT =
  '.txt,.tsv,.csv,.json,text/plain,text/csv,text/tab-separated-values,application/json';

const SNAPSHOT_KINDS = new Set<ReportKind>([
  'fba_inventory',
  'inventory_health',
  'restock_recommendations',
]);

const REPLACEABLE_CODES = new Set(['SNAPSHOT_DATE_TAKEN', 'PERIOD_TAKEN']);

// The chooser's grouping (everyday kinds first, the fetched kinds under
// "Manual (normally fetched automatically)") lives in lib/import-kinds.ts.
const CHOOSER_GROUPS = chooserGroups();

/** One kind's <option>, greyed out and labeled with why when it is disabled. */
function kindOption(k: ReportKind, kindInfo: Map<ReportKind, ImportKindInfo>) {
  const info = kindInfo.get(k);
  const enabled = info ? info.enabled : true;
  return (
    <option key={k} value={k} disabled={!enabled}>
      {KIND_LABELS[k]}
      {!enabled && info?.disabledReason ? ` — ${info.disabledReason}` : ''}
    </option>
  );
}

export function ImportsPage() {
  const session = useSession();
  const canImport = session.status === 'signedIn' && can(session.user.role, 'data:import');
  const queryClient = useQueryClient();
  const [reparseError, setReparseError] = useState<string | null>(null);
  const [reparseNote, setReparseNote] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();

  const tabParam = params.get('tab');
  const activeTab: ReportKind = REPORT_KINDS.includes(tabParam as ReportKind)
    ? (tabParam as ReportKind)
    : 'all_orders';

  function setTab(kind: ReportKind) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', kind);
        return next;
      },
      { replace: true },
    );
  }

  const imports = useQuery({
    queryKey: ['imports'],
    queryFn: () => api.get<{ imports: ReportFileView[] }>('/api/imports'),
    enabled: canImport,
  });
  const kinds = useQuery({
    queryKey: ['import-kinds'],
    queryFn: () => api.get<{ kinds: ImportKindInfo[] }>('/api/imports/kinds'),
    enabled: canImport,
  });
  const coverage = useQuery({
    queryKey: ['coverage'],
    queryFn: () => api.get<{ coverage: CoverageEntry[] }>('/api/imports/coverage'),
    enabled: canImport,
  });
  const analysis = useQuery({
    queryKey: ['analysis-status'],
    queryFn: () => api.get<AnalysisStatus>('/api/analysis/status'),
    enabled: canImport,
  });

  const reparse = useMutation({
    mutationFn: (id: number) => api.post<ImportOutcome>(`/api/imports/${id}/reparse`),
    onSuccess: async (result) => {
      setReparseError(null);
      const sweep = listingSweepText(
        result.counts.delisted,
        result.counts.relisted,
        result.counts.absent,
      );
      setReparseNote(
        `${KIND_LABELS[result.file.kind]}: ${integer(result.file.rowCount)} rows read, ` +
          `${integer(result.counts.inserted)} new, ${integer(result.counts.updated)} updated, ` +
          `${integer(result.counts.skipped)} already present${sweep ? `; ${sweep}` : ''}.`,
      );
      await invalidateAll(queryClient);
    },
    onError: async (caught: unknown) => {
      setReparseNote(null);
      setReparseError(caught instanceof ApiError ? caught.message : 'The re-parse failed.');
      await invalidateAll(queryClient);
    },
  });

  // The owner's "mark absent" action on an All Listings import: the file is taken
  // as the whole catalog and every SKU it leaves out is marked no longer listed.
  const markAbsent = useMutation({
    mutationFn: (id: number) =>
      api.post<{ file: ReportFileView; delisted: number }>(`/api/imports/${id}/delist-absent`),
    onSuccess: async (result) => {
      setReparseError(null);
      setReparseNote(
        `${integer(result.delisted)} SKU${result.delisted === 1 ? '' : 's'} marked no longer listed.`,
      );
      await invalidateAll(queryClient);
    },
    onError: async (caught: unknown) => {
      setReparseNote(null);
      setReparseError(
        caught instanceof ApiError ? caught.message : 'Marking the absent SKUs failed.',
      );
      await invalidateAll(queryClient);
    },
  });

  if (!canImport) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="Imports"
          description="Seller Central report downloads, and what each one loaded."
        />
        <Alert tone="info">Only an administrator can import reports or see the import log.</Alert>
      </div>
    );
  }

  const kindInfo = new Map(kinds.data?.kinds.map((k) => [k.kind, k]));
  const coverageByKind = new Map(coverage.data?.coverage.map((c) => [c.kind, c]));
  const allImports = imports.data?.imports ?? [];
  const tabImports = allImports.filter((file) => file.kind === activeTab);

  const tabItems: TabItem[] = REPORT_KINDS.map((kind) => {
    const forKind = allImports.filter((file) => file.kind === kind);
    const failed = forKind.filter((file) => file.status === 'failed').length;
    const warned = forKind.filter((file) => (file.warnings?.length ?? 0) > 0).length;
    const marks: string[] = [];
    if (failed > 0) marks.push(`${failed} failed`);
    if (warned > 0) marks.push(`${warned} with warnings`);
    return {
      id: kind,
      label: (
        <span className="inline-flex items-center gap-1.5">
          {KIND_LABELS[kind]}
          <span className="tabular rounded bg-raised px-1 text-xs text-muted">
            {forKind.length}
          </span>
          {(failed > 0 || warned > 0) && (
            <span
              className={`text-xs font-semibold ${failed > 0 ? 'text-danger-text' : 'text-warn-text'}`}
              title={marks.join(', ')}
            >
              ⚠ {failed > 0 ? failed : warned}
            </span>
          )}
        </span>
      ),
    };
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Imports"
        description="Upload one or many Seller Central report downloads of the same kind. Each file is kept as received, then loaded into the tables the other pages read."
      />

      <UploadCard kindInfo={kindInfo} />

      <SpapiConnectionCard kindLabels={KIND_LABELS} />

      {reparseError && <Alert>{reparseError}</Alert>}
      {reparseNote && <Alert tone="success">{reparseNote}</Alert>}

      {imports.isPending && <Spinner label="Loading imports" />}
      {imports.isError && <Alert>{imports.error.message}</Alert>}
      {imports.data && (
        <Card>
          <div className="overflow-x-auto border-b border-line px-3 py-2">
            <Tabs items={tabItems} active={activeTab} onChange={(id) => setTab(id as ReportKind)} />
          </div>
          <div role="tabpanel" className="space-y-3 p-3">
            {coverage.data && <CoverageStrip entry={coverageByKind.get(activeTab)} />}
            {activeTab === 'ledger_detail' &&
              analysis.data?.stockEstimated &&
              analysis.data.newestCensus && (
                <Alert tone="warning">
                  Stock history will be exact once an Inventory Ledger covering{' '}
                  {day(analysis.data.newestCensus)} is uploaded.
                </Alert>
              )}
            <TableScroll>
              <table className="w-full text-sm">
                <thead className="border-b border-line text-left text-xs text-muted">
                  <tr>
                    <th className="px-3 py-2 font-medium">File</th>
                    <th className="px-3 py-2 font-medium">Period</th>
                    <th className="px-3 py-2 text-right font-medium">Rows</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Uploaded</th>
                    <th className="px-3 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {tabImports.map((file) => (
                    <tr key={file.id}>
                      <td className="px-3 py-2">
                        <div className="text-body">{file.originalName ?? '—'}</div>
                        <div className="text-xs text-muted">{fileSize(file.byteSize)}</div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-muted">
                        {file.periodStart
                          ? `${day(file.periodStart)} – ${day(file.periodEnd)}`
                          : '—'}
                      </td>
                      <td className="tabular px-3 py-2 text-right text-body">
                        {integer(file.rowCount)}
                        {listingSweepText(
                          file.delistedCount,
                          file.relistedCount,
                          file.absentCount,
                        ) && (
                          <div className="whitespace-nowrap text-xs text-muted">
                            {listingSweepText(
                              file.delistedCount,
                              file.relistedCount,
                              file.absentCount,
                            )}
                          </div>
                        )}
                      </td>
                      <td className={`px-3 py-2 ${STATUS_TONE[file.status]}`}>
                        <div>{file.status}</div>
                        {file.error && (
                          <div className="max-w-md text-xs break-words text-danger-text">
                            {file.error}
                          </div>
                        )}
                        {(file.warnings?.length ?? 0) > 0 && (
                          <ul className="mt-1 max-w-md list-disc space-y-0.5 pl-4 text-xs break-words text-warn-text">
                            {file.warnings.map((warning) => (
                              <li key={warning}>{warning}</li>
                            ))}
                          </ul>
                        )}
                        {file.kind === 'all_listings' &&
                          file.status === 'parsed' &&
                          (file.absentCount ?? 0) > 0 &&
                          (file.superseded ? (
                            <div className="mt-1 text-xs text-muted">
                              Superseded by a newer listings file
                            </div>
                          ) : (
                            <MarkAbsentButton
                              count={file.absentCount ?? 0}
                              disabled={markAbsent.isPending}
                              onConfirm={() => markAbsent.mutate(file.id)}
                            />
                          ))}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-muted">
                        <div>{dateTime(file.uploadedAt)}</div>
                        <div className="text-xs">{file.uploadedByName ?? 'scheduled'}</div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          variant="ghost"
                          disabled={reparse.isPending}
                          onClick={() => reparse.mutate(file.id)}
                        >
                          Reparse
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {tabImports.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-sm text-muted">
                        No imports of this kind yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </TableScroll>
          </div>
        </Card>
      )}
    </div>
  );
}

async function invalidateAll(queryClient: ReturnType<typeof useQueryClient>) {
  await Promise.all(
    ['imports', 'coverage', 'summary', 'skus', 'restock'].map((key) =>
      queryClient.invalidateQueries({ queryKey: [key] }),
    ),
  );
}

/** The coverage strip at the top of a kind's tab: reach, gaps, and next pull. */
function CoverageStrip({ entry }: { entry: CoverageEntry | undefined }) {
  const [copied, setCopied] = useState(false);

  if (!entry) return <p className="text-xs text-muted">No data yet.</p>;
  const hasData =
    entry.from !== null || entry.through !== null || !!entry.skuCount || !!entry.distinctDays;
  if (!hasData) return <p className="text-xs text-muted">No data yet.</p>;

  let headline: string;
  if (entry.kind === 'all_listings') {
    headline = `SKU master as of ${day(entry.through)} — ${integer(entry.skuCount)} SKUs`;
  } else if (SNAPSHOT_KINDS.has(entry.kind)) {
    headline = `Snapshots from ${day(entry.from)} through ${day(entry.through)} — ${integer(
      entry.distinctDays,
    )} days`;
  } else {
    headline = `Data from ${day(entry.from)} through ${day(entry.through)}`;
  }

  const gapLabel =
    entry.kind === 'ledger_detail' ? 'Days with no ledger events' : 'Days with no orders';
  const range = entry.nextSuggestedRange;

  function copyRange() {
    if (!range || !navigator.clipboard) return;
    void navigator.clipboard.writeText(`${range.start} through ${range.end}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div className="rounded-md bg-sunken px-3 py-2 text-sm">
      <div className="text-body">{headline}</div>
      {entry.gaps.length > 0 && (
        <div className="mt-1 text-xs text-warn-text" title={entry.gaps.map(rangeText).join(', ')}>
          {gapLabel}: {summarizeList(entry.gaps.map(rangeText))} — a long run may mean a missed
          download.
        </div>
      )}
      {entry.missingMonths.length > 0 && (
        <div className="mt-1 text-xs text-warn-text" title={entry.missingMonths.join(', ')}>
          Missing months: {summarizeList(entry.missingMonths)}
        </div>
      )}
      {range ? (
        <div className="mt-1 flex items-center gap-2 text-xs text-muted">
          <span>
            Next download: <span className="text-body">{day(range.start)}</span> through{' '}
            <span className="text-body">{day(range.end)}</span>
            {range.more ? ` (then continue after ${day(range.end)})` : ''}
          </span>
          <Button variant="ghost" className="px-2 py-0.5 text-xs" onClick={copyRange}>
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      ) : (
        entry.note && <div className="mt-1 text-xs text-muted">{entry.note}</div>
      )}
    </div>
  );
}

function rangeText(range: { start: string; end: string }): string {
  return `${day(range.start)}–${day(range.end)}`;
}

function summarizeList(items: string[]): string {
  if (items.length <= 5) return items.join(', ');
  return `${items.slice(0, 5).join(', ')} +${items.length - 5} more`;
}

/**
 * The multi-file upload card. Reads and drives the shared queue from context;
 * the drop zone and file picker feed it, and the table renders each row with
 * its per-file date inputs, status, and the actions that fit its state.
 */
function UploadCard({ kindInfo }: { kindInfo: Map<ReportKind, ImportKindInfo> }) {
  const queue = useUploadQueue();
  const [dragging, setDragging] = useState(false);
  // The connection card's own status query (same key, so one request between
  // them): the nightly-fetch note is only true once the API is configured.
  const spapiStatus = useQuery({
    queryKey: ['spapi-status'],
    queryFn: () => api.get<SpapiStatus>('/api/spapi/status'),
  });
  const [inputKey, setInputKey] = useState(0);
  const { running, datePolicy, items } = queue;
  const t = totals(items);

  // A row uploads under its per-row override kind, or the batch kind; its date
  // inputs follow that kind's policy. The date column shows when any row needs it.
  const rowPolicy = (item: QueueItem<File>): DatePolicy =>
    datePolicyFor((item.overrideKind as ReportKind | null) ?? queue.kind);
  const showDates = datePolicy !== 'none' || items.some((item) => rowPolicy(item) !== 'none');

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    if (running) return;
    queue.addPickedFiles(event.dataTransfer.files);
  }

  return (
    <Card className="p-5">
      <h2 className="text-sm font-medium text-body">Upload reports</h2>

      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Report" hint={KIND_HINTS[queue.kind]}>
          <Select
            value={queue.kind}
            onChange={(e) => queue.setKind(e.target.value as ReportKind)}
            disabled={running}
          >
            {CHOOSER_GROUPS.everyday.map((k) => kindOption(k, kindInfo))}
            {/* Kinds the app fetches for itself; the manual upload stays for
                emergencies but is set apart so it is not the everyday choice. */}
            <optgroup label="Manual (normally fetched automatically)">
              {CHOOSER_GROUPS.manual.map((k) => kindOption(k, kindInfo))}
            </optgroup>
          </Select>
          {showFetchedNote(queue.kind, spapiStatus.data?.configured) && (
            <p className="mt-1 text-xs text-muted">
              The Amazon connection card below fetches this report nightly and on demand — use its
              “Fetch now” there. This manual upload is a fallback for when the API is unavailable.
            </p>
          )}
        </Field>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!running) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`mt-4 rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${
          dragging ? 'border-brand-600 bg-sunken' : 'border-line-strong'
        } ${running ? 'opacity-60' : ''}`}
      >
        <p className="text-sm text-body">Drop report files here, or choose them below.</p>
        <p className="mt-1 text-xs text-muted">
          Many at once is fine. Each file up to 50 MB; anything larger is marked and skipped. The
          same file is refused a second time.
        </p>
        <div className="mt-3 flex justify-center">
          <Input
            key={inputKey}
            type="file"
            multiple
            disabled={running}
            accept={ACCEPT}
            aria-label="Choose report files"
            onChange={(e) => {
              if (e.target.files) queue.addPickedFiles(e.target.files);
              setInputKey((k) => k + 1);
            }}
            className="max-w-xs file:mr-3 file:rounded file:border-0 file:bg-sunken file:px-2 file:py-1 file:text-xs file:text-body"
          />
        </div>
      </div>

      {items.length > 0 && (
        <TableScroll>
          <table className="mt-4 w-full text-sm" aria-label="Upload queue">
            <thead className="border-b border-line text-left text-xs text-muted">
              <tr>
                <th className="px-3 py-2 font-medium">File</th>
                <th className="px-3 py-2 text-right font-medium">Size</th>
                {showDates && (
                  <th className="px-3 py-2 font-medium">
                    {datePolicy === 'range' ? 'Period' : 'Report date'}
                  </th>
                )}
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Result</th>
                <th className="px-3 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="px-3 py-2 text-body">{item.name}</td>
                  <td className="tabular px-3 py-2 text-right whitespace-nowrap text-muted">
                    {fileSize(item.size)}
                  </td>
                  {showDates && (
                    <td className="px-3 py-2">
                      <DateCell item={item} />
                    </td>
                  )}
                  <td className={`px-3 py-2 whitespace-nowrap ${QUEUE_STATUS[item.status].tone}`}>
                    <span className="inline-flex items-center gap-2">
                      {item.status === 'uploading' && (
                        <span
                          className="h-3 w-3 animate-spin rounded-full border-2 border-line-strong border-t-body"
                          aria-hidden
                        />
                      )}
                      {QUEUE_STATUS[item.status].label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-muted">
                    <ResultCell item={item} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <RowActions item={item} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}

      {queue.finished && !running && items.length > 0 && (
        <div className="mt-4">
          <Alert tone={t.failed > 0 ? 'warning' : 'success'}>
            {summaryLine(t, queue.stopRequested)}
          </Alert>
        </div>
      )}

      <div className="mt-4 flex items-center justify-end gap-2">
        {running ? (
          <Button variant="secondary" onClick={queue.requestStop} disabled={queue.stopRequested}>
            {queue.stopRequested ? 'Stopping after current file…' : 'Stop after current file'}
          </Button>
        ) : (
          items.length > 0 && (
            <Button variant="ghost" onClick={queue.clear}>
              Clear
            </Button>
          )
        )}
        <Button type="button" onClick={queue.start} disabled={!queue.canStart}>
          {running
            ? 'Importing…'
            : t.queued > 0
              ? `Upload ${t.queued} ${t.queued === 1 ? 'file' : 'files'}`
              : 'Upload'}
        </Button>
      </div>
    </Card>
  );

  function DateCell({ item }: { item: QueueItem<File> }) {
    if (item.status === 'too_large') return <span className="text-muted">—</span>;
    const policy = rowPolicy(item);
    if (policy === 'none') return <span className="text-muted">—</span>;
    const editable = !running && item.status === 'queued';
    const isDup = queue.duplicateDates.has(item.id);
    if (policy === 'range') {
      return (
        <div className="flex items-center gap-1">
          <Input
            type="date"
            aria-label={`Period start for ${item.name}`}
            value={item.periodStart}
            disabled={!editable}
            onChange={(e) => queue.setRowPeriod(item.id, { start: e.target.value })}
            className="w-36"
          />
          <span className="text-muted">–</span>
          <Input
            type="date"
            aria-label={`Period end for ${item.name}`}
            value={item.periodEnd}
            disabled={!editable}
            onChange={(e) => queue.setRowPeriod(item.id, { end: e.target.value })}
            className="w-36"
          />
        </div>
      );
    }
    return (
      <div>
        <Input
          type="date"
          aria-label={`Report date for ${item.name}`}
          value={item.periodStart}
          disabled={!editable}
          onChange={(e) => queue.setRowDate(item.id, e.target.value)}
          className={`w-40 ${isDup ? 'ring-2 ring-danger-line' : ''}`}
        />
        {isDup && <div className="mt-1 text-xs text-danger-text">Same date as another file.</div>}
      </div>
    );
  }

  function RowActions({ item }: { item: QueueItem<File> }) {
    if (running) return null;
    const replaceable = item.status === 'failed' && item.code && REPLACEABLE_CODES.has(item.code);
    // A wrong-kind refusal names the kind the file really is; offer to re-queue
    // it under that kind when it is a kind we know and it is enabled. Any report
    // date or period the kind needs is enforced by the Upload button afterwards.
    const suggested =
      item.status === 'failed' &&
      item.suggestedKind &&
      (REPORT_KINDS as readonly string[]).includes(item.suggestedKind)
        ? (item.suggestedKind as ReportKind)
        : null;
    const suggestedEnabled = suggested ? (kindInfo.get(suggested)?.enabled ?? true) : false;
    return (
      <div className="flex justify-end gap-1">
        {suggested && suggestedEnabled && (
          <Button variant="ghost" onClick={() => queue.uploadAs(item.id, suggested)}>
            Upload as {KIND_LABELS[suggested]}
          </Button>
        )}
        {replaceable && (
          <Button variant="ghost" onClick={() => queue.replaceRow(item.id)}>
            Replace that day
          </Button>
        )}
        {item.status === 'failed' && (
          <Button variant="ghost" onClick={() => queue.retry(item.id)}>
            Retry
          </Button>
        )}
        {item.status !== 'uploading' && (
          <Button variant="ghost" onClick={() => queue.remove(item.id)}>
            Remove
          </Button>
        )}
      </div>
    );
  }
}

/**
 * "Mark the N absent SKUs as no longer listed" on an All Listings import that
 * left SKUs out without marking them: a hand upload (it could be a filtered
 * download) or a fetched file a guard stopped. The owner confirms that the file
 * is the whole catalog first.
 */
function MarkAbsentButton({
  count,
  disabled,
  onConfirm,
}: {
  count: number;
  disabled: boolean;
  onConfirm: () => void;
}) {
  const label = `Mark the ${integer(count)} absent SKU${count === 1 ? '' : 's'} as no longer listed`;
  return (
    <div className="mt-1">
      <Button
        variant="secondary"
        disabled={disabled}
        onClick={() => {
          if (
            window.confirm(
              `${label}? Only do this when the file is the whole All Listings Report, not an Active-only or Inactive-only download. A later listings file that carries a SKU lists it again.`,
            )
          ) {
            onConfirm();
          }
        }}
      >
        {label}
      </Button>
    </div>
  );
}

/** The counts, duplicate note, or error message for one finished row. */
function ResultCell({ item }: { item: QueueItem<File> }) {
  if (item.status === 'imported' && item.counts) {
    return (
      <div className="text-body">
        <div>
          {integer(item.rowCount)} rows: {integer(item.counts.inserted)} new,{' '}
          {integer(item.counts.updated)} updated, {integer(item.counts.skipped)} already present
          {listingSweepText(item.counts.delisted, item.counts.relisted, item.counts.absent) &&
            `; ${listingSweepText(item.counts.delisted, item.counts.relisted, item.counts.absent)}`}
        </div>
        {item.warnings.length > 0 && (
          <ul className="mt-1 max-w-md list-disc space-y-0.5 pl-4 text-xs text-warn-text">
            {item.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }
  if (item.status === 'duplicate') {
    return (
      <span>Already imported{item.duplicateDate ? ` on ${day(item.duplicateDate)}` : ''}.</span>
    );
  }
  if (item.status === 'too_large') {
    return <span className="text-danger-text">Over 50 MB — not sent.</span>;
  }
  if (item.status === 'failed' && item.error) {
    return <span className="max-w-md break-words text-danger-text">{item.error}</span>;
  }
  return <span className="text-muted">—</span>;
}

/** The one-line tally shown when a run finishes. */
function summaryLine(t: ReturnType<typeof totals>, stopped: boolean): string {
  const parts = [
    `${t.imported} imported`,
    `${t.duplicate} ${t.duplicate === 1 ? 'duplicate' : 'duplicates'}`,
    `${t.failed} failed`,
  ];
  if (t.tooLarge > 0) parts.push(`${t.tooLarge} too large`);
  const line = parts.join(', ') + '.';
  const tail = [];
  if (stopped && t.queued > 0) tail.push(`Stopped early; ${t.queued} still queued.`);
  return [line, ...tail].join(' ');
}
